// src/utils/constants.js

export const FOLDER_TEMPLATE = [
  { 
    code: '01', 
    name: 'Study Setup', 
    desc: 'Protocol, SAP, ICF, study design documents',
    children: [
      { code: '01-01', name: 'Protocol & Amendments', desc: 'Final protocol and amendments' },
      { code: '01-02', name: 'ICF & Patient Materials', desc: 'Informed consent forms and patient info' },
      { code: '01-03', name: 'SAP', desc: 'Statistical Analysis Plan' }
    ]
  },
  { 
    code: '02', 
    name: 'Feasibility', 
    desc: 'Feasibility assessments, site evaluations',
    children: [
      { code: '02-01', name: 'Feasibility Reports', desc: 'CRO feasibility assessments' },
      { code: '02-02', name: 'Site Evaluations', desc: 'Site feasibility questionnaires' }
    ]
  },
  { 
    code: '03', 
    name: 'Contracts', 
    desc: 'Agreements, NDAs, budgets',
    children: [
      { code: '03-01', name: 'CRO Contracts', desc: 'Master service agreements' },
      { code: '03-02', name: 'Site Contracts', desc: 'Site agreements and budgets' }
    ]
  },
  { 
    code: '04', 
    name: 'Regulatory', 
    desc: 'Submissions, approvals, ethics committee',
    children: [
      { code: '04-01', name: 'EC Submissions', desc: 'Ethics committee submissions' },
      { code: '04-02', name: 'Regulatory Approvals', desc: 'CDSCO/FDA/EMA approvals' },
      { code: '04-03', name: 'Safety Reports', desc: 'SAE and safety monitoring' }
    ]
  },
  { 
    code: '05', 
    name: 'Project Management', 
    desc: 'Plans, trackers, meeting minutes',
    children: [
      { code: '05-01', name: 'Project Plans', desc: 'Project management plans' },
      { code: '05-02', name: 'Trackers', desc: 'Milestone and task trackers' },
      { code: '05-03', name: 'Meeting Minutes', desc: 'Team meeting records' },
      { code: '05-04', name: 'Communications', desc: 'Email and correspondence logs' }
    ]
  },
  { 
    code: '06', 
    name: 'Quality & Risk', 
    desc: 'Risk registers, quality metrics',
    children: [
      { code: '06-01', name: 'Risk Registers', desc: 'Study risk registers' },
      { code: '06-02', name: 'Quality Metrics', desc: 'Quality and performance metrics' },
      { code: '06-03', name: 'Audits & Inspections', desc: 'Audit reports and findings' }
    ]
  },
  { 
    code: '07', 
    name: 'Clinical Ops', 
    desc: 'Site management, monitoring, training',
    children: [
      { code: '07-01', name: 'Site Management', desc: 'Site selection and management' },
      { code: '07-02', name: 'Monitoring Reports', desc: 'CRA monitoring visit reports' },
      { code: '07-03', name: 'Training Materials', desc: 'Staff training documents' }
    ]
  },
  { 
    code: '08', 
    name: 'Data Management', 
    desc: 'EDC, CRFs, data specs',
    children: [
      { code: '08-01', name: 'EDC Specifications', desc: 'EDC build specifications' },
      { code: '08-02', name: 'CRFs', desc: 'Case Report Forms' },
      { code: '08-03', name: 'Data Validation', desc: 'Data validation rules' },
      { code: '08-04', name: 'Data Transfer', desc: 'Data transfer agreements' }
    ]
  },
  { 
    code: '09', 
    name: 'Bioanalytical', 
    desc: 'Method validation, sample analysis',
    children: [
      { code: '09-01', name: 'Method Validation', desc: 'Bioanalytical method validation' },
      { code: '09-02', name: 'Sample Analysis', desc: 'Sample analysis reports' },
      { code: '09-03', name: 'Quality Controls', desc: 'QC and standards' }
    ]
  },
  { 
    code: '10', 
    name: 'Final Reports', 
    desc: 'CSR, publications, regulatory submissions',
    children: [
      { code: '10-01', name: 'Clinical Study Report', desc: 'Final CSR' },
      { code: '10-02', name: 'Publication-ready', desc: 'Manuscripts and posters' },
      { code: '10-03', name: 'Regulatory Submissions', desc: 'CTD and submission packages' }
    ]
  }
];

// Helper function to flatten folder structure for lookups
export const flattenFolders = () => {
  const flat = [];
  FOLDER_TEMPLATE.forEach(f => {
    flat.push({ code: f.code, name: f.name, desc: f.desc, parent: null });
    if (f.children) {
      f.children.forEach(c => {
        flat.push({ code: c.code, name: c.name, desc: c.desc, parent: f.code });
      });
    }
  });
  return flat;
};

export const FLAT_FOLDERS = flattenFolders();

export const getFolderByCode = (code) => {
  return FLAT_FOLDERS.find(f => f.code === code) || null;
};

export const getParentFolder = (code) => {
  const folder = FLAT_FOLDERS.find(f => f.code === code);
  if (folder && folder.parent) {
    return FLAT_FOLDERS.find(f => f.code === folder.parent);
  }
  return null;
};

export const getFolderPath = (code) => {
  const path = [];
  let current = getFolderByCode(code);
  while (current) {
    path.unshift(current.name);
    current = getParentFolder(current.code);
  }
  return path.join(' / ');
};

export const STD_TEMPLATE = {
  id: 'std_babe_v3',
  name: 'Standard BABE Feasibility v3',
  version: 3,
  sections: [
    {
      id: 'sec_exp',
      name: 'CRO experience',
      weight: 10,
      questions: [
        { id: 'q1', text: 'Has the CRO conducted ≥3 similar BABE studies in last 3 years?', type: 'yesno', weight: 10 },
        { id: 'q2', text: 'Does the CRO have direct experience with the specific therapeutic area?', type: 'yesno', weight: 8 }
      ]
    },
    {
      id: 'sec_cap',
      name: 'Capacity & recruitment',
      weight: 10,
      questions: [
        { id: 'q3', text: 'Can the CRO start the project within the required timeline?', type: 'scale', weight: 8 },
        { id: 'q4', text: 'Is the required participant population available within timeline?', type: 'scale', weight: 10 }
      ]
    },
    {
      id: 'sec_reg',
      name: 'Regulatory & ethics',
      weight: 8,
      questions: [
        { id: 'q5', text: 'Does the CRO have experience with required regulatory/ethics process?', type: 'yesno', weight: 8 },
        { id: 'q6', text: 'Are existing EC relationships in place at proposed sites?', type: 'scale', weight: 5 }
      ]
    },
    {
      id: 'sec_lab',
      name: 'Bioanalytical capability',
      weight: 10,
      questions: [
        { id: 'q7', text: 'Is the bioanalytical method validated for the required analyte?', type: 'yesno', weight: 10 },
        { id: 'q8', text: 'Is appropriate LC-MS/MS or comparable instrumentation available?', type: 'yesno', weight: 8 }
      ]
    },
    {
      id: 'sec_qua',
      name: 'Quality system',
      weight: 10,
      questions: [
        { id: 'q9', text: 'Are SOPs, validation, training and audit trail controls available?', type: 'yesno', weight: 10 },
        { id: 'q10', text: 'Is the inspection/audit history within last 24 months free of critical findings?', type: 'yesno', weight: 8 }
      ]
    },
    {
      id: 'sec_tec',
      name: 'Technology',
      weight: 6,
      questions: [
        { id: 'q11', text: 'Can the CRO support secure document exchange and project tracking?', type: 'scale', weight: 6 }
      ]
    },
    {
      id: 'sec_bud',
      name: 'Budget & commercial',
      weight: 5,
      questions: [
        { id: 'q12', text: 'Is the proposed budget within the acceptable range?', type: 'scale', weight: 5 },
        { id: 'q13', text: 'Are payment terms acceptable?', type: 'scale', weight: 3 }
      ]
    }
  ]
};

export const REPORT_TEMPLATES = [
  {
    id: 'tpl_babe_feas',
    code: 'BABE-FEAS-001',
    name: 'BA/BE Feasibility Report',
    desc: 'Bioanalytical + clinical-operational + regulatory + risk summary.',
    sections: ['Cover & metadata', 'Purpose & scope', 'Product information', 'Statistical & PK parameters', 'Clinical-operational feasibility', 'Regulatory feasibility', 'Risk summary', 'Conclusion & recommendation'],
    primary: true
  },
  {
    id: 'tpl_lit',
    code: 'LIT-SUM-001',
    name: 'Literature search summary',
    desc: 'Structured summary of a search session.',
    sections: ['Query', 'Scope', 'Sources', 'Findings', 'Conflicts', 'Gaps']
  },
  {
    id: 'tpl_mol',
    code: 'MOL-KNO-001',
    name: 'Molecule knowledge dossier',
    desc: 'One-page dossier per molecule pulling all approved sources.',
    sections: ['Profile', 'PK summary', 'Regulatory', 'Historical Arcolab data']
  },
  {
    id: 'tpl_reg',
    code: 'REG-GAP-001',
    name: 'Regulatory gap analysis',
    desc: 'Compares study design against jurisdictional requirements.',
    sections: ['Submission scope', 'Requirements matrix', 'Gaps', 'Mitigation']
  },
  {
    id: 'tpl_risk',
    code: 'RISK-EVD-001',
    name: 'Risk evidence brief',
    desc: 'Risks with citation-backed mitigation suggestions.',
    sections: ['Heatmap', 'Top risks', 'Evidence', 'Owners & timeline']
  },
  {
    id: 'tpl_proj',
    code: 'PROJ-BRF-001',
    name: 'Project status brief',
    desc: 'Executive one-pager for portfolio review.',
    sections: ['Status', 'Milestones', 'Risks', 'Asks']
  }
];

export const MODULES = {
  dashboard: { id: 'dashboard', name: 'Dashboard', icon: 'ti-layout-dashboard', group: 'workspace' },
  studies: { id: 'studies', name: 'Studies & Documents', icon: 'ti-folder', group: 'workspace' },
  feasibility: { id: 'feasibility', name: 'Feasibility', icon: 'ti-checklist', group: 'workspace' },
  project: { id: 'project', name: 'Project tracking', icon: 'ti-route', group: 'workspace' },
  risk: { id: 'risk', name: 'Risk analytics', icon: 'ti-shield', group: 'workspace' },
  knowledge: { id: 'knowledge', name: 'Knowledge library', icon: 'ti-books', group: 'intelligence' },
  search: { id: 'search', name: 'Knowledge search', icon: 'ti-message-chatbot', group: 'intelligence' },
  reports: { id: 'reports', name: 'Reports', icon: 'ti-file-text', group: 'intelligence' },
  audit: { id: 'audit', name: 'Audit trail', icon: 'ti-history', group: 'governance' },
  rbac: { id: 'rbac', name: 'Roles & permissions', icon: 'ti-users', group: 'governance' },
  admin: { id: 'admin', name: 'Admin', icon: 'ti-settings', group: 'governance' }
};

export const GROUPS = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'intelligence', label: 'Intelligence engine' },
  { id: 'governance', label: 'Governance' }
];